"use client"

import { ServicePillar, BookingAppointment, LocationId, LOCATIONS, getLocationPricing } from "@/data/booking-appointments"
import { AppointmentOptionCard } from "../ui/AppointmentOptionCard"
import { ArrowLeft } from "lucide-react"

interface AppointmentTypeStepProps {
  pillar: ServicePillar
  location: LocationId
  onSelect: (appointment: BookingAppointment) => void
  onBack: () => void
}

/**
 * AppointmentTypeStep - Choose Initial, Return, or Extended appointment
 *
 * Now shows definitive pricing for the selected location (no price ranges).
 * Shows Initial and Return options for all pillars.
 * Extended appointment is shown only for General Health.
 */
export function AppointmentTypeStep({
  pillar,
  location,
  onSelect,
  onBack,
}: AppointmentTypeStepProps) {
  // Build appointments array including extended if available
  const appointments: BookingAppointment[] = [pillar.initial, pillar.return]
  if (pillar.extended) {
    appointments.push(pillar.extended)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="text-center">
          <h2 className="text-xl font-normal text-[#3f4141] mb-2 mt-6">
            Choose your appointment
          </h2>
          <p className="text-sm font-light text-[#3f4141] mt-1">
            {LOCATIONS[location]} / {pillar.name}
          </p>
        </div>
      </div>

      {/* Appointment Options */}
      <div className="space-y-4">
        {appointments.map((appointment) => {
          const pricing = getLocationPricing(appointment, location)
          return (
            <AppointmentOptionCard
              key={appointment.id}
              appointment={appointment}
              price={pricing?.price}
              onSelect={() => onSelect(appointment)}
            />
          )
        })}
      </div>

      {/* Help text */}
      <p className="text-sm text-gray-500 font-light text-center px-4">
        Not sure which to choose? Select <strong>Initial Appointment</strong> if
        this is your first visit or you're returning after a long break.
      </p>
    </div>
  )
}
